import { Fragment, KeyboardEvent } from 'react';
import { Box, Typography, TypographyProps } from '@mui/material';

export interface InlineViewOption<TValue extends string> {
    value: TValue;
    label: string;
}

interface InlineViewToggleProps<TValue extends string> {
    options: InlineViewOption<TValue>[];
    value: TValue;
    onChange: (next: TValue) => void;
    /** Heading level to blend into. `subtitle1` is what the budget section headings use. */
    variant?: TypographyProps['variant'];
}

// A section heading that IS its own switch: two (or more) bold titles separated by a dimmed slash, the
// inactive ones dashed-underlined. Used where a screen shows one of several same-shaped tables, so the
// choice costs no extra chrome — the Budget overview's "Handling Data / Costs" and, since CALL-783, every
// scenario type's input⇄impact pair.
//
// Extracted from BudgetOverview, where it was inline JSX, WITH the keyboard and ARIA support that version
// lacked: a plain Typography has no role and no tab stop, so the only way to switch views was a mouse.
const InlineViewToggle = <TValue extends string>({
    options,
    value,
    onChange,
    variant = 'subtitle1',
}: InlineViewToggleProps<TValue>) => {
    const activate = (next: TValue) => {
        if (next !== value) onChange(next);
    };

    const onKeyDown = (event: KeyboardEvent<HTMLElement>, next: TValue) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        // Space scrolls the page by default — a control that answers to it has to consume it.
        event.preventDefault();
        activate(next);
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            {options.map((option, index) => {
                const isActive = option.value === value;
                return (
                    <Fragment key={option.value}>
                        {index > 0 && (
                            <Typography variant={variant} fontWeight="bold" sx={{ opacity: 0.4 }} aria-hidden>
                                /
                            </Typography>
                        )}
                        <Typography
                            variant={variant}
                            fontWeight="bold"
                            role="button"
                            tabIndex={0}
                            aria-pressed={isActive}
                            onClick={() => activate(option.value)}
                            onKeyDown={(event) => onKeyDown(event, option.value)}
                            sx={{
                                cursor: isActive ? 'default' : 'pointer',
                                opacity: isActive ? 1 : 0.5,
                                textDecoration: isActive ? 'none' : 'underline dashed',
                                textUnderlineOffset: 3,
                            }}
                        >
                            <span>{option.label}</span>
                        </Typography>
                    </Fragment>
                );
            })}
        </Box>
    );
};

export default InlineViewToggle;
